import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import { Box, Button, Dialog, Stack } from '@mui/material'

import DownloadIcon from '@mui/icons-material/Download'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import errorHandlerHook from '@hooks/useErrorHandler'
import responsiveHook from '@hooks/useResponsive'
import toastHook from '@hooks/useToast'

import LeadService from '@services/leads'

import { TLGTypography } from '@components/TLGTypography'
import { TextField } from '@components/TextField'

import { LeadSource } from '@utils/types'

import {
  buttonsContainerSx,
  dialogContainerSx,
  fieldsWrapperSx,
  formContainerSx,
  loadingButtonSx,
  submitButtonIconSx,
  textSx,
} from './styles'

type FormData = {
  firstName: string
  lastName: string
  companyName: string
  companyEmail: string
}

const MAX_NAME_LENGTH = 64
const MAX_EMAIL_LENGTH = 80

const INITIAL_VALUES = {
  firstName: '',
  lastName: '',
  companyName: '',
  companyEmail: '',
}

interface ILeadMagnetDialogProps {
  title: string
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
}

const LeadMagnetDialog = ({
  title,
  isOpen,
  onClose,
  onSubmit,
}: ILeadMagnetDialogProps) => {
  const { t } = useTranslation()

  const { isMobile } = responsiveHook.useResponsive()

  const { show: showToast } = toastHook.useToast()

  const SCHEMA = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t('First name must be {{maxLengthName}} characters or less', {
          maxLengthName: MAX_NAME_LENGTH,
        })
      )
      .required(t('First name is mandatory')),
    lastName: Yup.string()
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t('Last name must be {{maxLengthName}} characters or less', {
          maxLengthName: MAX_NAME_LENGTH,
        })
      )
      .required(t('Last name is mandatory')),
    companyEmail: Yup.string()
      .trim()
      .email(t('Invalid email'))
      .required(t('Company e-mail is mandatory')),
    companyName: Yup.string()
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t('Company name must be {{maxLengthName}} characters or less', {
          maxLengthName: MAX_NAME_LENGTH,
        })
      ),
  })

  const handleOnSubmit = async ({
    firstName,
    lastName,
    companyEmail,
    companyName,
  }: FormData) => {
    try {
      await LeadService.create({
        lead: {
          firstName,
          lastName,
          companyEmail,
          companyName,
        },
        source: LeadSource.WHITEPAPER_LEAD_MAGNET,
      })
      onSubmit()
      showToast({
        type: 'success',
        message: t('White paper downloaded successfully'),
      })
    } catch (err) {
      handleError({ exception: err })
    } finally {
      formik.resetForm()
      onClose()
    }
  }

  const formik = useFormik<FormData>({
    validationSchema: SCHEMA,
    validateOnBlur: false,
    validateOnMount: true,
    validateOnChange: true,
    initialValues: INITIAL_VALUES,
    onSubmit: handleOnSubmit,
  })

  const { handleError } = errorHandlerHook.useErrorHandler(formik)

  const isSubmitButtonDisabled = !formik.isValid || !formik.dirty

  return (
    <Dialog
      data-testid="white-paper-lead-magnet-dialog"
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      open={isOpen}
      onClose={onClose}
    >
      <Box sx={dialogContainerSx}>
        <Stack
          component="form"
          onSubmit={formik.handleSubmit}
          sx={formContainerSx}
        >
          <Box>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={textSx}
            >
              {title}
            </TLGTypography>
            <Box sx={fieldsWrapperSx}>
              <TextField
                fullWidth
                aria-label={t('First Name')}
                label={t('First Name*')}
                size="small"
                name="firstName"
                variant="filled"
                value={formik.values.firstName}
                error={!!formik.errors.firstName && !!formik.touched.firstName}
                helperText={
                  !!formik.touched.firstName ? formik.errors.firstName : ''
                }
                inputProps={{
                  'data-testid': 'first-name-field',
                  maxLength: MAX_NAME_LENGTH,
                }}
                showMaxLengthInHelperText
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                fullWidth
                aria-label={t('Last Name')}
                label={t('Last Name*')}
                size="small"
                name="lastName"
                variant="filled"
                value={formik.values.lastName}
                error={!!formik.errors.lastName && !!formik.touched.lastName}
                helperText={
                  formik.touched.lastName ? formik.errors.lastName : ''
                }
                inputProps={{
                  'data-testid': 'last-name-field',
                  maxLength: MAX_NAME_LENGTH,
                }}
                showMaxLengthInHelperText
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Box>
            <Box sx={fieldsWrapperSx}>
              <TextField
                fullWidth
                aria-label={t('Company Email')}
                label={t('Company Email*')}
                size="small"
                name="companyEmail"
                variant="filled"
                value={formik.values.companyEmail}
                error={
                  !!formik.errors.companyEmail && !!formik.touched.companyEmail
                }
                helperText={
                  formik.touched.companyEmail ? formik.errors.companyEmail : ''
                }
                inputProps={{
                  'data-testid': 'company-email-field',
                  maxLength: MAX_EMAIL_LENGTH,
                }}
                showMaxLengthInHelperText
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                fullWidth
                aria-label={t('Company Name')}
                label={t('Company Name')}
                size="small"
                name="companyName"
                variant="filled"
                value={formik.values.companyName}
                error={
                  !!formik.errors.companyName && !!formik.touched.companyName
                }
                helperText={
                  formik.touched.companyName ? formik.errors.companyName : ''
                }
                inputProps={{
                  'data-testid': 'company-name-field',
                  maxLength: MAX_NAME_LENGTH,
                }}
                showMaxLengthInHelperText
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Box>
          </Box>

          <Box sx={buttonsContainerSx}>
            <Button
              type="button"
              data-testid="modal-close-button"
              onClick={onClose}
              variant="text"
              size={isMobile ? 'small' : 'medium'}
              disabled={formik.isSubmitting}
            >
              {t('Cancel')}
            </Button>

            <LoadingButton
              type="submit"
              data-testid="download-white-paper-confirmation-button"
              loading={formik.isSubmitting}
              disabled={isSubmitButtonDisabled}
              variant="contained"
              sx={loadingButtonSx}
              color="secondary"
              size={isMobile ? 'small' : 'medium'}
              endIcon={<DownloadIcon sx={submitButtonIconSx} />}
            >
              {!isSubmitButtonDisabled
                ? t('Download White Paper')
                : t('Complete the form')}
            </LoadingButton>
          </Box>
        </Stack>
      </Box>
    </Dialog>
  )
}

export { LeadMagnetDialog }
